import {HttpClient, HttpClientEnhancer} from '../HttpClient';
import {Jwt, JwtManager, SHARED_MUTEX, type RefreshedTokenData} from '../JwtManager';

// shadowing fetch to make sure only fetchFn is used
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fetch = undefined;

export interface FetchRefreshOptions {
    jwtManager: JwtManager;
}

export const withJwtAuthorization = (jwtManager: JwtManager): HttpClientEnhancer => {
    const mutex = SHARED_MUTEX;

    const addAuthorizationHeader = <T extends HttpClient.Init | void>(token: Jwt, init: T): T => {
        const headers = new Headers(init?.headers);
        headers.set('Authorization', `Bearer ${token}`);
        return {...init, headers};
    };

    return http => async (url, init) => {
        await mutex.waitForUnlock();
        const token = jwtManager.getJwt();
        if (!token) {
            throw new Error(`Missing jwt when calling protected endpoint: ${url}`);
        }
        let response = await http(url, addAuthorizationHeader(token, init));
        if (response.status === 401) {
            if (mutex.isLocked()) {
                await mutex.waitForUnlock();
                const token = jwtManager.getJwt();
                if (token) {
                    response = await http(url, addAuthorizationHeader(token, init));
                }
            } else {
                const release = await mutex.acquire();
                try {
                    const refreshed: RefreshedTokenData | null = await jwtManager.fetchNewRefreshToken();
                    if (refreshed) {
                        jwtManager.persistJwt(refreshed.access_token);
                        jwtManager.persistRefreshToken(refreshed.refresh_token);
                        response = await http(url, addAuthorizationHeader(refreshed.access_token, init));
                    }
                } finally {
                    release();
                }
            }
        }
        return response;
    };
};
